import { useState, useCallback, useMemo } from 'react';

/**
 * Scoring logic hook
 * Handles star ratings and takeover score calculation
 */
export const useScoring = (initialScores = {}) => {
  const [scores, setScores] = useState({
    contentQuality: 5,
    followsInstructions: 5,
    deliversOnTime: 5,
    fastTurnaround: null, // No default - must be explicitly set
    ...initialScores
  });

  /**
   * Update a specific score
   */
  const updateScore = useCallback((field, value) => {
    setScores(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  /**
   * Update all scores at once
   */
  const setAllScores = useCallback((newScores) => {
    setScores(newScores);
  }, []);

  /**
   * Reset scores to defaults
   */
  const resetScores = useCallback(() => {
    setScores({
      contentQuality: 5,
      followsInstructions: 5,
      deliversOnTime: 5,
      fastTurnaround: null  // No default - must be explicitly set
    });
  }, []);

  /**
   * Calculate takeover score (0-15 scale based on ratings only)
   */
  const takeoverScore = useMemo(() => {
    const { contentQuality, followsInstructions, deliversOnTime } = scores;

    // Simple sum of the three 0-5 ratings = 0-15 total
    const totalScore = (contentQuality || 0) + (followsInstructions || 0) + (deliversOnTime || 0);

    return Math.min(15, totalScore);
  }, [scores]);

  /**
   * Get score display info
   */
  const getScoreInfo = useMemo(() => {
    const maxScore = 15; // Using 0-15 scale
    const percentage = Math.round((takeoverScore / maxScore) * 100);

    let level = 'poor';
    let color = 'error';

    if (percentage >= 90) { // 14-15 points
      level = 'excellent';
      color = 'success';
    } else if (percentage >= 80) { // 12-13 points
      level = 'very good';
      color = 'info';
    } else if (percentage >= 70) { // 10-11 points
      level = 'good';
      color = 'primary';
    } else if (percentage >= 60) { // 9 points
      level = 'fair';
      color = 'warning';
    }

    return {
      score: takeoverScore,
      maxScore,
      percentage,
      level,
      color
    };
  }, [takeoverScore]);

  /**
   * Fast turnaround options
   */
  const fastTurnaroundOptions = [
    { value: 'Yes', label: 'Yes' },
    { value: 'No', label: 'No' },
    { value: 'Sometimes', label: 'Sometimes' }
  ];

  /**
   * Rating fields configuration
   */
  const ratingFields = [
    {
      key: 'contentQuality',
      label: 'Content Quality',
      description: 'Overall quality of content created'
    },
    {
      key: 'followsInstructions',
      label: 'Follows Instructions',
      description: 'Adherence to campaign requirements'
    },
    {
      key: 'deliversOnTime',
      label: 'Delivers on Time',
      description: 'Timeliness of content delivery'
    }
  ];

  return {
    // State
    scores,
    takeoverScore,
    scoreInfo: getScoreInfo,

    // Actions
    updateScore,
    setAllScores,
    resetScores,

    // Configuration
    ratingFields,
    fastTurnaroundOptions,

    // Helpers
    isMaxScore: takeoverScore === 15,
    isMinScore: takeoverScore === 0
  };
};